import React, { useState, useEffect } from 'react';
import { useIntl } from 'react-intl';
import { useQuery } from '@apollo/client';
import { Swiper, SwiperSlide } from 'swiper/react';
import { Navigation } from 'swiper/modules';
import { useDispatch } from 'react-redux';
import withStyles from 'isomorphic-style-loader/withStyles';

// Components
import NoDataSection from './NoDataSection';
import ServicePlansItems from './UserPlan/ServicePlansItems';
import { NextIconSvg, PrevIconSvg } from '../Common/Assets/SvgIconComponent/CommonSvgIcon';
import SearchFilterPlans from './SearchFilterPlans';
import Loader from '../Loader/Loader';

// Helpers
import { isRTL } from '../../helpers/formatLocale';
import messages from '../../locale/jsonMessages';
import { getUserPurchasePlans } from '../../lib/graphql/servicePlans';
import history from '../../core/history';
import { cancelPurchaseServicePlanAction as changePurchaseServicePlanAction } from '../../actions/ServicePlans/makeServicePlanPayment/makeServicePlanPayment';
import CancelServicePlanModalBody from './PurchaseHistory/CancelServicePlanModalBody';
import CustomConfirmModal from '../Common/CustomConfirmModal/CustomConfirmModal';
import ChangePlanModalBody from './ChangePlanModalBody';
import { setPaymentActiveStart } from '../../actions/paymentState';

// Images
import NoServicePlanDataImg from '/public/SiteIcons/noServicePlan.svg';

import cs from 'swiper/swiper-bundle.css';
import bt from '../../components/commonStyle.css';
import t from '../../styles/customSwiper.css';


function SampleNextArrow({ className, onClick }) {
    return (
        <button className={className} onClick={onClick}>
            <NextIconSvg className="commonSwiperNavIcon" />
        </button>
    );
}

function SamplePrevArrow({ className, onClick }) {
    return (
        <button className={className} onClick={onClick}>
            <PrevIconSvg className="commonSwiperNavIcon" />
        </button>
    );
}


const UserPlansContainer = (props) => {
    const { data: { getAllUserServicePlans, refetch }, mode } = props;

    const { locale, formatMessage } = useIntl();
    const [showModal, setShowModal] = useState(false);
    const [modalData, setModalData] = useState(null);
    const [load, setLoad] = useState(false);
    const [searchKey, setSearchKey] = useState("");
    const [userType, setUserType] = useState(mode || 'renter');


    const rtl = isRTL(locale);

    const dispatch = useDispatch();

    const { data: userPlansData, loading, refetch: purchaseDataRefetch } = useQuery(getUserPurchasePlans, {
        fetchPolicy: 'network-only',
        ssr: false,
        variables: {
            userType: mode
        }
    });

    const params = {
        modules: [Navigation],
        slidesPerView: 'auto',
        spaceBetween: 12,
        grabCursor: true,
        navigation: {
            nextEl: `.servicePlanSwiperNavNextBtn`,
            prevEl: `.servicePlanSwiperNavPrevBtn`,
        },
        breakpoints: {
            320: { slidesPerView: 1 },
            576: { slidesPerView: 'auto' },
        }
    };

    const openChangeModal = (id, planId) => {
        setShowModal(true);
        setModalData({ id, planId, changeModal: true });
    };


    const setStateVariable = (variables) => {
        setSearchKey(variables?.searchKey);
        setUserType(variables?.userType);
    };


    const handleSearchChange = (searchValue) => {
        const variables = { searchKey: searchValue, userType };
        setStateVariable(variables);
        refetch(variables);
    };

    const handleChange = async ({ id, planId }) => {
        setLoad(true);
        setLoad(false);
        setShowModal(false);
        setModalData(null);
        dispatch(setPaymentActiveStart(true));
        history.push(`/users/service-payment/${mode}-${id}`);
    };


    const handleCancel = async ({ id }) => {
        try {
            setLoad(true);
            await dispatch(changePurchaseServicePlanAction(id));
            setLoad(false);
            setShowModal(false);
            setModalData(null);
            setStateVariable({ searchKey, userType: mode });
            refetch({ searchKey, userType: mode });
            purchaseDataRefetch()
        } catch (error) {
            setLoad(false);
        }
    };

    const tableFilter = () => [
        { value: "renter", label: formatMessage(messages.guestPlansLabel) },
        { value: "owner", label: formatMessage(messages.hostPlansLabel) }
    ];

    const handleDropDown = async (e) => {
        const value = e.target.value;
        const variables = { userType: value };
        setStateVariable(variables);
        history.push(`/users/service-plans/${value}`);
    };

    const openCancelModal = (value) => {
        setModalData({ value, cancelModal: true });
        setShowModal(true);
    };

    return (
        <div>
            {showModal && modalData && modalData?.changeModal && (
                <CustomConfirmModal
                    modalStatus={showModal}
                    showModel={showModal}
                    title={formatMessage(messages.changePlanLabel)}
                    closeModal={() => setShowModal(false)}
                    popupButtonLeftName={formatMessage(messages.cancel)}
                    popupButtonLeftFunction={() => setShowModal(false)}
                    loaderActionBtnText={formatMessage(messages.next)}
                    load={load}
                    loaderConfirmActionFunction={() =>
                        handleChange({
                            id: modalData?.id,
                            planId: modalData?.planId
                        })}
                >
                    <ChangePlanModalBody />
                </CustomConfirmModal>
            )}

            {showModal && modalData && modalData?.cancelModal &&
                <CustomConfirmModal
                    modalStatus={showModal}
                    showModel={showModal}
                    title={formatMessage(messages.cancelPlanLabel)}
                    closeModal={() => setShowModal(false)}
                    popupButtonLeftName={formatMessage(messages.close)}
                    popupButtonLeftFunction={() => setShowModal(false)}
                    loaderActionBtnText={formatMessage(messages.confirmLabel)}
                    load={load}
                    loaderConfirmActionFunction={() =>
                        handleCancel({
                            id: modalData?.value?.id,
                        })
                    }
                >
                    <CancelServicePlanModalBody modalData={modalData} />
                </CustomConfirmModal>
            }

            {getAllUserServicePlans?.count > 0 &&
                <SearchFilterPlans
                    handleSearchChange={handleSearchChange}
                    tableFilter={tableFilter()}
                    placeholder={formatMessage(messages.searchPlansLabel)}
                    handleDropDown={handleDropDown}
                    selectedValue={userType}
                    containerClass={bt.spaceBottom3}
                />}

            {getAllUserServicePlans?.results?.length === 0 &&
                <NoDataSection
                    notFoundImg={NoServicePlanDataImg}
                    noDataTitle={formatMessage(messages.noServicePlanLabel)}
                    noDataDesc={formatMessage(messages.servicePlanContent)}
                    noDataNavBtnLink="/contact"
                    navLinkBtnText={formatMessage(messages.servicePlanContentOne)}
                    containerClass="servicePlanHistoryNoDataBox"
                />}

            {getAllUserServicePlans?.results?.length > 0 && (
                <div className="servicePlanSwiperContainer">
                    <div className='positionRelative'>
                        <Swiper {...params} dir={rtl ? 'rtl' : 'ltr'} key={rtl} className='servicePlanSwiperRow'>
                            {getAllUserServicePlans.results.map((item, index) => (
                                <SwiperSlide key={index} className="servicePlanSwiper">
                                    <ServicePlansItems
                                        data={item}
                                        mode={mode}
                                        openChangeModal={openChangeModal}
                                        openCancelModal={openCancelModal}
                                        userPlansData={userPlansData?.getUserPurchasePlans?.result}
                                    />
                                </SwiperSlide>
                            ))}
                            <SwiperSlide className="dummyLastServicePlanSwiper">
                            </SwiperSlide>
                        </Swiper>
                        <SampleNextArrow className='servicePlanSwiperNavNextBtn' />
                        <SamplePrevArrow className='servicePlanSwiperNavPrevBtn' />
                    </div>
                </div>
            )}
        </div>
    );
};

export default withStyles(cs, bt, t)(UserPlansContainer);
